import React, { useState, useMemo } from 'react';
import moment from 'moment';

import { CardContainer, CardTitle } from 'components/CardContainer/CardContainer';
import { SmallButton, SmallButtonLink } from 'components/Button/SmallButton';
import { Tags } from 'components/Tags/Tags';
import { colors } from 'global/colors';
import { getConfig } from 'functions/getConfig';
import { userRoles } from 'constants/users';
import { categories } from 'constants/tests';
import YesNoModal from 'components/Modals/yesNoModal';
import { CardDetails, CardDetailsAndTagContainer, DetailsLabel } from 'components/CardContainer/styles';
import { ButtonContainer, DetailsContainer } from './styles';

export default function CustomTestCard({ test, user, publishOrArchiveTest, copyTest }) {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [modalInfo, setModalInfo] = useState({});

  const getEditedAtTime = () => {
    let time = test.editedAt ? test.editedAt : test.createdAt;
    let ago;
    let date = moment(time)._d;
    if (!time) {
      ago = '-';
    } else {
      ago = moment(date).fromNow();
    }
    return ago;
  };

  const isCopyTestVisible = () => {
    if (user.role === userRoles.SUPER_ADMIN && test.company === user.adminCompany) {
      return true;
    } else if (
      [userRoles.COMPANY_ADMIN, userRoles.DEPARTMENT_ADMIN, userRoles.EMPLOYEE].includes(user.role) &&
      test.company === user.company
    ) {
      return true;
    } else if (user.role === userRoles.RESELLER && test.company === user.resellerId) {
      return true;
    } else if (test.account === user._id || test.publicToCommunity) {
      return true;
    }
    return false;
  };

  const isPublishOrArchiveVisible = () => {
    if (user.role === userRoles.SUPER_ADMIN) {
      return true;
    } else if (
      [userRoles.COMPANY_ADMIN, userRoles.DEPARTMENT_ADMIN, userRoles.EMPLOYEE].includes(user.role) &&
      test.company === user.company
    ) {
      return true;
    } else if (user.role === userRoles.RESELLER && test.company === user.resellerId) {
      return true;
    }
    return false;
  };

  const isEditVisible = useMemo(() => {
    const isCode = [categories.CODE.category, categories.CHALLENGE.category, categories.EXERCISE.category].includes(
      test.category
    );
    if (user.role === userRoles.SUPER_ADMIN && (isCode || test.company === user.adminCompany)) {
      return true;
    } else if (
      [userRoles.COMPANY_ADMIN, userRoles.DEPARTMENT_ADMIN, userRoles.EMPLOYEE].includes(user.role) &&
      test.company === user.company
    ) {
      return true;
    } else if (user.role === userRoles.RESELLER && test.company === user.resellerId) {
      return true;
    } else if (user.role === userRoles.CANDIDATE && test.account === user._id) {
      return true;
    }
    return false;
  }, [test, user]);

  const isQuestionFeedbackVisible = () => {
    if (
      test.category === categories.CODE.category ||
      test.category === categories.CHALLENGE.category ||
      test.category === categories.EXERCISE.category
    )
      return false;
    return user.role === userRoles.SUPER_ADMIN || (test.department && test.department === user.department);
  };

  const isViewImplementationVisible = () => {
    return (
      !isEditVisible &&
      test.publicToCommunity &&
      test.account !== user._id &&
      [categories.CHALLENGE.category, categories.EXERCISE.category].includes(test.category)
    );
  };

  const setPublishOrArchiveTest = (isConfirmed) => {
    setShowConfirmationModal(false);
    if (isConfirmed) {
      if (modalInfo.isPublishOrArchiveModal)
        publishOrArchiveTest({ test: modalInfo.test, operation: modalInfo.operation });
      else copyTest({ _id: modalInfo._id, isMultichoice: modalInfo.isMultichoice });
    }
    setModalInfo({});
  };

  const getEditorUrl = () => {
    if (!isEditVisible) return;
    const isCode = [categories.CODE.category, categories.CHALLENGE.category, categories.EXERCISE.category].includes(
      test.category
    );
    if (isCode) return `${getConfig().REACT_URL}/freecode-creator/${test._id}`;
    else return `${getConfig().REACT_URL}/multichoice-creator/${test._id}/`;
  };

  return (
    <CardContainer link={getEditorUrl()} image={test.image} height="auto">
      <CardTitle name={test.name} categoryName={test.categoryName} />
      {showConfirmationModal && (
        <YesNoModal modalInfo={modalInfo} confirm={(isConfirmed) => setPublishOrArchiveTest(isConfirmed)} />
      )}
      <CardDetailsAndTagContainer>
        <DetailsContainer>
          <CardDetails>
            <DetailsLabel>Created At:&nbsp;</DetailsLabel>
            {moment(test.createdAt).format('ll')}
          </CardDetails>
          <CardDetails>
            <DetailsLabel>Edited At:&nbsp;</DetailsLabel>
            {getEditedAtTime()}
          </CardDetails>
          {test.owner && (
            <CardDetails>
              <DetailsLabel>Owner:&nbsp;</DetailsLabel>
              {test.owner}
            </CardDetails>
          )}
          {test.averageRating && (
            <CardDetails>
              <DetailsLabel>Average Rating:&nbsp;</DetailsLabel>
              {test.averageRating}
            </CardDetails>
          )}
          {!isNaN(test.totalParticipants) && (
            <CardDetails>
              <DetailsLabel>Total Participants:&nbsp;</DetailsLabel>
              {test.totalParticipants}
            </CardDetails>
          )}
        </DetailsContainer>
        <Tags
          isTestTag={true}
          tags={[test.published ? 'Published' : test.custom ? 'Custom' : 'Archived']}
          tagColor={test.published ? colors.green2 : test.custom ? colors.yellow : colors.red}
        />
      </CardDetailsAndTagContainer>
      <ButtonContainer>
        {isQuestionFeedbackVisible() && (
          <SmallButtonLink
            iconName="fa fa-external-link"
            link={`${getConfig().REACT_URL}/questionFeedback`}
            label="Feedback"
          />
        )}
        {isPublishOrArchiveVisible() && (
          <SmallButton
            iconName="fa fa-bullhorn"
            label={test.archived ? 'Publish' : test.custom ? 'Publish' : 'Archive'}
            onClick={() => {
              setShowConfirmationModal(true);
              setModalInfo({
                title: 'Confirmation',
                body: `Do you want to ${test.archived ? 'publish' : test.custom ? 'publish' : 'archive'} the test?`,
                operation: test.archived ? 'publishTest' : test.custom ? 'publishTest' : 'archiveTest',
                test: test._id,
                isPublishOrArchiveModal: true,
              });
            }}
          />
        )}
        {isCopyTestVisible() && (
          <SmallButton
            iconName="fa fa-copy"
            label="Copy"
            onClick={() => {
              setShowConfirmationModal(true);
              setModalInfo({
                title: 'Confirmation',
                body: `Do you want to copy ${test.name}?`,
                isMultichoice:
                  test.category === categories.MULTICHOICE.category ||
                  test.category === categories.GENERAL_SKILLS.category,
                _id: test._id,
              });
            }}
          />
        )}
        {isEditVisible && <SmallButtonLink iconName="fa fa-edit" label="Edit" link={getEditorUrl()} />}
        {isViewImplementationVisible() && (
          <SmallButtonLink
            iconName="fa fa-edit"
            label="View Implementation"
            link={`${getConfig().REACT_URL}/freecode-creator-inspect/${test._id}`}
          />
        )}
      </ButtonContainer>
    </CardContainer>
  );
}
